/**
 * Markets — main authenticated dashboard.
 *
 * Clean static layout (no draggable grid). Hierarchy: hero stats →
 * scanner + briefing side-by-side → activity strip at the bottom.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Box, Chip, IconButton, Tooltip, Typography, Button } from '@mui/material';
import { Refresh, ArrowForward } from '@mui/icons-material';
import { motion } from 'framer-motion';

import {
  fetchWatchlist,
  fetchAllRecentEvents,
  fetchPerformanceSummary,
  fetchRecentlyTriggeredAlerts,
  fetchBriefing,
  fetchMarketsOverview,
  addToWatchlist,
} from './marketsSlice';
import { fetchUserSettings } from '../userSettings/userSettingsSlice';
import { fetchStrategies } from '../visualStrategies/visualStrategiesSlice';

import HeroStatStrip from './panels/HeroStatStrip';
import BriefingPanel from './panels/BriefingPanel';
import Panel from './panels/Panel';
import {
  ScannerWidget,
  RegimeHeatmap,
  RecentEventsWidget,
  SignalsWidget,
  AlertsWidget,
  PerformanceWidget,
} from './widgets';

// Stagger and delay values tuned for perceived performance. The
// previous values (stagger 0.08, delay 0.1, duration 0.4) added
// ~0.5 s of intentional visual latency before anything rendered —
// cute on a warm cache, painful on a cold first load where the user
// is already waiting for API data. The new values make the skeleton
// structure appear near-instantly so the user sees "the page is
// loading" rather than a blank screen.
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.04,
      delayChildren: 0,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25 } },
};

// Suggested defaults seeded into an empty watchlist on user request.
// Mix of crypto (BTC, ETH) and big tech (NVDA, AAPL, TSLA). Deliberately
// excludes GOLD until proper commodity data is available.
const SUGGESTED_DEFAULT_WATCHLIST = ['BTC/USD', 'ETH/USD', 'NVDA', 'AAPL', 'TSLA'];

const Markets = () => {
  const dispatch = useDispatch();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [addingDefaults, setAddingDefaults] = useState(false);
  const briefingRef = useRef(null);
  const scannerRef = useRef(null);

  const tags = useSelector((state) => state.markets.tags || []);
  const strategies = useSelector((state) => state.visualStrategies.strategies);
  const strategiesLoading = useSelector((state) => state.visualStrategies.loading);
  const user = useSelector((state) => state.account.user);
  const hasNoStrategies = !strategiesLoading && user && strategies.length === 0;

  useEffect(() => {
    // Combined overview call replaces three separate round trips for
    // tags + performance summary + watchlist — the biggest win for
    // first-load perceived speed. The remaining calls (settings,
    // strategies, events, briefing) fire in parallel alongside it.
    // Duplicate dispatches from child widgets are skipped by each
    // thunk's in-flight condition guard (see marketsSlice).
    dispatch(fetchMarketsOverview());
    dispatch(fetchUserSettings());
    dispatch(fetchStrategies());
    dispatch(fetchAllRecentEvents());
    dispatch(fetchBriefing());
  }, [dispatch]);

  const handleAddDefaults = async () => {
    setAddingDefaults(true);
    try {
      // Add each default symbol sequentially. addToWatchlist is a thunk
      // that updates the watchlist Redux state on each fulfilled action,
      // so the briefing/scanner will start populating as the symbols land.
      for (const symbol of SUGGESTED_DEFAULT_WATCHLIST) {
        // eslint-disable-next-line no-await-in-loop
        await dispatch(addToWatchlist(symbol));
      }
      // Refresh the panels that depend on the watchlist
      dispatch(fetchWatchlist({ force: true }));
      dispatch(fetchPerformanceSummary({ force: true }));
      if (briefingRef.current?.refresh) {
        briefingRef.current.refresh();
      }
    } finally {
      setAddingDefaults(false);
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    dispatch(fetchWatchlist({ force: true }));
    dispatch(fetchAllRecentEvents({ force: true }));
    dispatch(fetchPerformanceSummary({ force: true }));
    dispatch(fetchRecentlyTriggeredAlerts());
    // Ask the scanner widget to refresh itself with its *current* symbol
    // list rather than dispatching fetchScannerData from here. Doing it
    // here would race against fetchWatchlist — if the server watchlist
    // had diverged, a stale forced fetch could resolve after the widget's
    // own effect-driven fetch and stomp the correct data.
    if (scannerRef.current?.refresh) {
      scannerRef.current.refresh();
    }
    if (briefingRef.current?.refresh) {
      briefingRef.current.refresh();
    }
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  return (
    <Box
      sx={{
        backgroundColor: 'var(--dwlf-bg)',
        minHeight: '100vh',
        py: { xs: 3, md: 5 },
        px: { xs: 2, md: 5 },
      }}
    >
      <motion.div variants={containerVariants} initial="hidden" animate="visible">
        {/* Header */}
        <motion.div variants={itemVariants}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'flex-end' },
              justifyContent: 'space-between',
              gap: 2,
              mb: { xs: 3, md: 5 },
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: '"IBM Plex Mono", monospace',
                  fontSize: '0.65rem',
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: 'var(--dwlf-accent-60)',
                  mb: 1,
                }}
              >
                Markets · Live
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Space Grotesk", sans-serif',
                  fontSize: { xs: '1.75rem', md: '2.25rem' },
                  fontWeight: 500,
                  letterSpacing: '-0.02em',
                  color: 'var(--dwlf-text)',
                  lineHeight: 1.1,
                }}
              >
                Today at a glance
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              {tags.slice(0, 6).map((tag) => (
                <Chip
                  key={tag}
                  label={tag.toUpperCase()}
                  component={Link}
                  to={`/tags/${tag}`}
                  clickable
                  size="small"
                  sx={{
                    fontFamily: '"IBM Plex Mono", monospace',
                    fontSize: '0.65rem',
                    letterSpacing: '0.08em',
                    borderRadius: 0,
                    backgroundColor: 'transparent',
                    border: '1px solid var(--dwlf-ink-12)',
                    color: 'var(--dwlf-ink-55)',
                    height: 28,
                    '&:hover': {
                      borderColor: 'var(--dwlf-accent)',
                      backgroundColor: 'var(--dwlf-accent-06)',
                      color: 'var(--dwlf-accent)',
                    },
                  }}
                />
              ))}
              <Tooltip title="Refresh data">
                <IconButton
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  sx={{
                    borderRadius: 0,
                    border: '1px solid var(--dwlf-ink-12)',
                    color: 'var(--dwlf-ink-55)',
                    width: 36,
                    height: 36,
                    '&:hover': {
                      borderColor: 'var(--dwlf-accent)',
                      color: 'var(--dwlf-accent)',
                      backgroundColor: 'var(--dwlf-accent-06)',
                    },
                  }}
                >
                  <Refresh sx={{ fontSize: '1.1rem' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </motion.div>

        {/* Onboarding nudge for users with no strategies */}
        {hasNoStrategies && (
          <motion.div variants={itemVariants}>
            <Box
              sx={{
                mb: 3,
                px: 3,
                py: 2,
                borderLeft: '3px solid var(--dwlf-accent)',
                backgroundColor: 'var(--dwlf-accent-05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"IBM Plex Mono", monospace',
                  fontSize: '0.85rem',
                  color: 'var(--dwlf-ink-70)',
                }}
              >
                New to DWLF? Build your first strategy in under five minutes.
              </Typography>
              <Button
                component={Link}
                to="/learn"
                endIcon={<ArrowForward sx={{ fontSize: '0.9rem' }} />}
                sx={{
                  fontFamily: '"Space Mono", monospace',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'var(--dwlf-accent)',
                  borderRadius: 0,
                  border: '1px solid var(--dwlf-accent-30)',
                  px: 2,
                  py: 0.75,
                  '&:hover': {
                    backgroundColor: 'var(--dwlf-accent-10)',
                    borderColor: 'var(--dwlf-accent)',
                  },
                }}
              >
                Start here
              </Button>
            </Box>
          </motion.div>
        )}

        {/* Hero stat strip */}
        <motion.div variants={itemVariants}>
          <Box sx={{ mb: { xs: 3, md: 4 } }}>
            <HeroStatStrip />
          </Box>
        </motion.div>

        {/* Main grid: Scanner + Briefing. Briefing gets a bit more room
            now that we've dropped the Watch column from the scanner. */}
        <motion.div variants={itemVariants}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' },
              gap: { xs: 3, md: 3 },
              mb: { xs: 3, md: 4 },
            }}
          >
            <Panel eyebrow="Your watchlist" title="Live events">
              <ScannerWidget ref={scannerRef} />
            </Panel>
            <BriefingPanel
              ref={briefingRef}
              onAddDefaults={handleAddDefaults}
              addingDefaults={addingDefaults}
            />
          </Box>
        </motion.div>

        {/* Regime heatmap + Performance side-by-side. The heatmap gives
            a single-glance view of where the user's whole watchlist sits
            structurally, complementing the per-symbol detail above. */}
        <motion.div variants={itemVariants}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: { xs: 3, md: 3 },
              mb: { xs: 3, md: 4 },
            }}
          >
            <Panel eyebrow="Watchlist overview" title="Regime heatmap">
              <RegimeHeatmap />
            </Panel>
            <Panel eyebrow="Performance" title="Best & worst movers">
              <PerformanceWidget />
            </Panel>
          </Box>
        </motion.div>

        {/* Activity strip: Recent Events / Recent Signals / Alerts */}
        <motion.div variants={itemVariants}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
              gap: { xs: 3, md: 3 },
            }}
          >
            <Panel eyebrow="Activity" title="Recent events">
              <RecentEventsWidget />
            </Panel>
            <Panel eyebrow="Activity" title="Recent signals">
              <SignalsWidget />
            </Panel>
            <Panel eyebrow="Activity" title="Recent alerts">
              <AlertsWidget />
            </Panel>
          </Box>
        </motion.div>
      </motion.div>
    </Box>
  );
};

export default Markets;
